/**
 * CHG-052 REQ-V52-019: 프로젝트 이슈 타입 설정 탭
 */
import { useState, useMemo } from 'react';
import { Plus, X, RotateCcw, Minus, GitBranch } from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import {
  useProjectIssueTypeConfig,
  useAddOverride,
  useRemoveOverride,
  useChangeProjectScheme,
} from '../../hooks/useProjectIssueTypeConfig';
import { useIssueTypes } from '../../hooks/useIssueTypeAdmin';
import { useHierarchyOverrides, useAddHierarchyOverride, useRemoveHierarchyOverride } from '../../hooks/useIssueMove';
import { DynamicIcon } from '../ui/DynamicIcon';
import SchemeSelectModal from './SchemeSelectModal';
import Spinner from '../ui/Spinner';

interface Props {
  projectId: string;
  isProjectManager: boolean;
}

export default function ProjectIssueTypeSettingsTab({ projectId, isProjectManager }: Props) {
  const { data: config, isLoading } = useProjectIssueTypeConfig(projectId);
  const { data: allTypes } = useIssueTypes();
  const changeSchemeMutation = useChangeProjectScheme();
  const addOverrideMutation = useAddOverride();
  const removeOverrideMutation = useRemoveOverride();

  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [showAddHierarchy, setShowAddHierarchy] = useState(false);
  const [hParent, setHParent] = useState<string>('__root__');
  const [hChild, setHChild] = useState<string>('');
  const { data: hierarchy } = useHierarchyOverrides(projectId);
  const addHierarchyMut = useAddHierarchyOverride();
  const removeHierarchyMut = useRemoveHierarchyOverride();
  const [showAddType, setShowAddType] = useState(false);
  const [showRemoveType, setShowRemoveType] = useState(false);

  const addOverrides = useMemo(
    () => (config?.overrides ?? []).filter((o) => o.action === 'ADD'),
    [config?.overrides],
  );
  const removeOverrides = useMemo(
    () => (config?.overrides ?? []).filter((o) => o.action === 'REMOVE'),
    [config?.overrides],
  );

  // 스킴에 포함되지 않고 ADD 오버라이드에도 없는 타입 = 추가 가능
  const availableToAdd = useMemo(() => {
    if (!config) return [];
    const schemeTypeIds = new Set(config.issueTypes.map((t) => t.issueTypeId));
    return (allTypes ?? []).filter(
      (t) => !schemeTypeIds.has(t.issueTypeId) && !addOverrides.find((o) => o.issueType.issueTypeId === t.issueTypeId),
    );
  }, [config, allTypes, addOverrides]);

  // 스킴에 포함되어 있고 REMOVE 오버라이드가 아닌 타입 = 제거 가능
  const removableTypes = useMemo(
    () => (config?.issueTypes ?? []).filter(
      (t) => !removeOverrides.find((o) => o.issueType.issueTypeId === t.issueTypeId),
    ),
    [config?.issueTypes, removeOverrides],
  );

  if (isLoading) return <Spinner />;
  if (!config) return null;

  const handleChangeScheme = async (schemeId: string) => {
    try {
      await changeSchemeMutation.mutateAsync({ projectId, schemeId });
      toast.success('스킴이 변경되었습니다.');
      setShowSchemeModal(false);
    } catch (err) {
      toast.error(isAxiosError(err) ? (err.response?.data as Record<string, string>)?.error ?? '스킴 변경에 실패했습니다.' : '스킴 변경에 실패했습니다.');
    }
  };

  const handleAddOverride = async (issueTypeId: string) => {
    try {
      await addOverrideMutation.mutateAsync({ projectId, issueTypeId, action: 'ADD' });
      toast.success('타입이 추가되었습니다.');
      setShowAddType(false);
    } catch (err) {
      toast.error(isAxiosError(err) ? (err.response?.data as Record<string, string>)?.error ?? '추가에 실패했습니다.' : '추가에 실패했습니다.');
    }
  };

  const handleRemoveOverride = async (issueTypeId: string) => {
    try {
      await addOverrideMutation.mutateAsync({ projectId, issueTypeId, action: 'REMOVE' });
      toast.success('타입이 제거되었습니다.');
      setShowRemoveType(false);
    } catch (err) {
      toast.error(isAxiosError(err) ? (err.response?.data as Record<string, string>)?.error ?? '제거에 실패했습니다.' : '제거에 실패했습니다.');
    }
  };

  const handleRestoreOverride = async (issueTypeId: string) => {
    try {
      await removeOverrideMutation.mutateAsync({ projectId, issueTypeId });
      toast.success('오버라이드가 복원되었습니다.');
    } catch (err) {
      toast.error(isAxiosError(err) ? (err.response?.data as Record<string, string>)?.error ?? '복원에 실패했습니다.' : '복원에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-6">
      {/* 현재 스킴 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">현재 스킴</h3>
          {isProjectManager && (
            <button
              onClick={() => setShowSchemeModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
            >
              스킴 변경
            </button>
          )}
        </div>

        {config.scheme ? (
          <>
            <p className="text-sm font-medium text-gray-800 mb-1">{config.scheme.name}</p>
            {config.scheme.description && (
              <p className="text-sm text-gray-500 mb-3">{config.scheme.description}</p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400">스킴이 설정되지 않았습니다</p>
        )}

        {/* 사용 가능 타입 */}
        <div className="mt-3">
          <p className="text-sm font-medium text-gray-500 mb-2">사용 가능한 이슈 타입</p>
          <div className="flex flex-wrap gap-2">
            {config.issueTypes.map((t) => (
              <div
                key={t.issueTypeId}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full border border-gray-100"
              >
                <DynamicIcon name={t.icon} color={t.color} customIconPath={t.customIconPath} size={14} />
                <span className="text-sm text-gray-700">{t.name}</span>
                {t.isDefault && <span className="text-[9px] text-blue-500 font-medium">(기본)</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 오버라이드 */}
      {isProjectManager && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">프로젝트 오버라이드</h3>

          {/* ADD 오버라이드 */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">추가된 타입</p>
              <div className="relative">
                <button
                  onClick={() => setShowAddType(!showAddType)}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-green-600 hover:bg-green-50 rounded"
                >
                  <Plus size={12} /> 타입 추가
                </button>
                {showAddType && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 max-h-48 overflow-y-auto">
                    {availableToAdd.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-gray-400">추가 가능한 타입이 없습니다</p>
                    ) : (
                      availableToAdd.map((t) => (
                        <button
                          key={t.issueTypeId}
                          onClick={() => handleAddOverride(t.issueTypeId)}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <DynamicIcon name={t.icon} color={t.color} customIconPath={t.customIconPath} size={14} />
                          {t.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            {addOverrides.length > 0 ? (
              <div className="space-y-1">
                {addOverrides.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                    <DynamicIcon name={o.issueType.icon} color={o.issueType.color} customIconPath={o.issueType.customIconPath} size={14} />
                    <span className="text-sm text-gray-700 flex-1">{o.issueType.name}</span>
                    <button
                      onClick={() => handleRestoreOverride(o.issueType.issueTypeId)}
                      className="p-0.5 text-gray-400 hover:text-red-500"
                      title="제거"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">추가된 타입이 없습니다</p>
            )}
          </div>

          {/* REMOVE 오버라이드 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">제거된 타입</p>
              <div className="relative">
                <button
                  onClick={() => setShowRemoveType(!showRemoveType)}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                >
                  <Minus size={12} /> 타입 제거
                </button>
                {showRemoveType && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 max-h-48 overflow-y-auto">
                    {removableTypes.map((t) => (
                      <button
                        key={t.issueTypeId}
                        onClick={() => handleRemoveOverride(t.issueTypeId)}
                        className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <DynamicIcon name={t.icon} color={t.color} customIconPath={t.customIconPath} size={14} />
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {removeOverrides.length > 0 ? (
              <div className="space-y-1">
                {removeOverrides.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg">
                    <DynamicIcon name={o.issueType.icon} color={o.issueType.color} customIconPath={o.issueType.customIconPath} size={14} className="opacity-50" />
                    <span className="text-sm text-gray-500 flex-1 line-through">{o.issueType.name}</span>
                    <button
                      onClick={() => handleRestoreOverride(o.issueType.issueTypeId)}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 text-sm text-blue-600 hover:bg-blue-50 rounded"
                      title="복원"
                    >
                      <RotateCcw size={10} /> 복원
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">제거된 타입이 없습니다</p>
            )}
          </div>
        </div>
      )}

      {/* 계층 규칙 오버라이드 */}
      {isProjectManager && hierarchy && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GitBranch size={16} className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">계층 규칙 오버라이드</h3>
            </div>
            <button
              onClick={() => setShowAddHierarchy(true)}
              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
            >
              <Plus size={12} /> 규칙 추가
            </button>
          </div>

          {/* 유효 규칙 — 상위 타입별 카드 그룹 */}
          {(() => {
            // 상위 타입별 그룹핑
            const groups = new Map<string, { parentType: typeof hierarchy.effectiveRules[0]['parentType']; children: typeof hierarchy.effectiveRules }>();
            for (const r of hierarchy.effectiveRules) {
              const key = r.parentType?.issueTypeId ?? '__root__';
              if (!groups.has(key)) groups.set(key, { parentType: r.parentType, children: [] });
              groups.get(key)!.children.push(r);
            }
            const groupArr = [...groups.values()];

            return (
              <div className={`grid gap-3 ${groupArr.length >= 4 ? 'grid-cols-4' : groupArr.length >= 3 ? 'grid-cols-3' : groupArr.length >= 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {groupArr.map((group) => {
                  const parentKey = group.parentType?.issueTypeId ?? '__root__';
                  return (
                    <div key={parentKey} className="bg-gray-50 rounded-lg p-2.5">
                      {/* 상위 타입 헤더 */}
                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-gray-200">
                        {group.parentType ? (
                          <>
                            <DynamicIcon name={group.parentType.icon} size={13} />
                            <span className="text-sm font-semibold text-gray-700">{group.parentType.name}</span>
                          </>
                        ) : (
                          <span className="text-sm font-semibold text-gray-500">루트 (최상위)</span>
                        )}
                        <span className="text-[11px] text-gray-400 ml-auto">{group.children.length}</span>
                      </div>
                      {/* 하위 타입 목록 */}
                      <div className="space-y-0.5">
                        {group.children.map((r, idx) => {
                          const isOverride = r.source === 'override';
                          return (
                            <div key={idx} className={`flex items-center gap-1.5 text-sm px-1.5 py-0.5 rounded ${isOverride ? 'bg-green-100 border border-green-200 border-dashed' : ''}`}>
                              <span className="text-gray-300 text-[11px]">└</span>
                              <DynamicIcon name={r.childType.icon} size={11} />
                              <span className="text-gray-700">{r.childType.name}</span>
                              {isOverride && <span className="text-green-600 text-[9px]">+</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* 오버라이드 관리 */}
          {hierarchy.overrides.length > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">활성 오버라이드</p>
              {hierarchy.overrides.map((o) => (
                <div key={o.id} className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded ${o.action === 'ADD' ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span className={`text-[11px] px-1 rounded ${o.action === 'ADD' ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>{o.action}</span>
                  <span className="text-gray-500">{o.parentType?.name ?? '루트'} → {o.childType.name}</span>
                  <button
                    onClick={async () => {
                      try {
                        await removeHierarchyMut.mutateAsync({ projectId, overrideId: o.id });
                        toast.success('오버라이드가 제거되었습니다.');
                      } catch (err: any) {
                        toast.error(err?.response?.data?.error ?? '제거에 실패했습니다.');
                      }
                    }}
                    className="ml-auto text-gray-400 hover:text-red-500"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 계층 규칙 추가 모달 */}
      {showAddHierarchy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-80 p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">계층 규칙 추가</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">부모 타입</label>
                <select value={hParent} onChange={(e) => setHParent(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5">
                  <option value="__root__">루트 (최상위)</option>
                  {config.issueTypes.map((t) => (
                    <option key={t.issueTypeId} value={t.issueTypeId}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">자식 타입</label>
                <select value={hChild} onChange={(e) => setHChild(e.target.value)} className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5">
                  <option value="">선택하세요</option>
                  {config.issueTypes.map((t) => (
                    <option key={t.issueTypeId} value={t.issueTypeId}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowAddHierarchy(false); setHChild(''); }} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">취소</button>
              <button
                onClick={async () => {
                  if (!hChild) return;
                  try {
                    await addHierarchyMut.mutateAsync({
                      projectId,
                      data: { parentTypeId: hParent === '__root__' ? null : hParent, childTypeId: hChild, action: 'ADD' },
                    });
                    toast.success('계층 규칙이 추가되었습니다.');
                    setShowAddHierarchy(false);
                    setHChild('');
                  } catch (err: any) {
                    toast.error(err?.response?.data?.error ?? '추가에 실패했습니다.');
                  }
                }}
                disabled={!hChild}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >추가</button>
            </div>
          </div>
        </div>
      )}

      {/* 스킴 선택 모달 */}
      {showSchemeModal && (
        <SchemeSelectModal
          projectId={projectId}
          currentSchemeId={config.scheme?.schemeId}
          onSelect={handleChangeScheme}
          onClose={() => setShowSchemeModal(false)}
          applying={changeSchemeMutation.isPending}
        />
      )}
    </div>
  );
}
